import { TwitchViewersService, type Viewer } from '../lib/twitchViewers';
import { twitchService } from '../lib/twitch';
import Swal from 'sweetalert2';
import './ViewersComponent.css';
import './ViewersComponentUpdated.css'; // Added new CSS file for updated styles

export class ViewersComponent {
  private viewersList!: HTMLElement;
  private searchInput!: HTMLInputElement;
  private sortSelect!: HTMLSelectElement;
  private totalViewersElement!: HTMLElement;
  private realViewersElement!: HTMLElement;
  private knownBotsElement!: HTMLElement;
  private viewersHeader!: HTMLElement;
  private viewersService: TwitchViewersService;
  private viewers: Viewer[] = [];
  private currentFilter: string = 'all';
  private container: HTMLElement;
  private currentUser: any;

  constructor(container: HTMLElement) {
    this.container = container;
    this.currentUser = twitchService.getUser();
    const elements = this.getRequiredElements(container);
    Object.assign(this, elements);

    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    this.viewersService = new TwitchViewersService(
      import.meta.env.VITE_TWITCH_CLIENT_ID,
      accessToken
    );

    // Initialize event handlers
    this.viewersList.addEventListener('click', this.handleViewerClick.bind(this));
    this.viewersList.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      const actionButton = target.closest('.action-button');
      if (actionButton) {
        e.preventDefault();
        e.stopPropagation();
        await this.handlePreviewButtonClick(e);
      }
    });
    
    this.initializeEventListeners();
  }

  private getRequiredElements(container: HTMLElement) {
    const viewersList = container.querySelector('.viewers-list');
    const searchInput = container.querySelector('.viewers-search');
    const sortSelect = container.querySelector('.viewers-sort');
    const totalViewersElement = container.querySelector('#totalViewers');
    const realViewersElement = container.querySelector('#realViewers');
    const knownBotsElement = container.querySelector('#knownBots');
    const viewersHeader = container.querySelector('.viewers-header');

    if (!viewersList || !searchInput || !sortSelect || 
        !totalViewersElement || !realViewersElement || !knownBotsElement || !viewersHeader) {
      throw new Error('Required viewers tab elements not found');
    }

    return {
      viewersList: viewersList as HTMLElement,
      searchInput: searchInput as HTMLInputElement,
      sortSelect: sortSelect as HTMLSelectElement,
      totalViewersElement: totalViewersElement as HTMLElement,
      realViewersElement: realViewersElement as HTMLElement,
      knownBotsElement: knownBotsElement as HTMLElement,
      viewersHeader: viewersHeader as HTMLElement
    };
  }

  private initializeEventListeners(): void {
    console.log('Initializing event listeners');
    
    // Initialize search input
    this.searchInput.addEventListener('input', () => this.filterAndSortViewers());
    
    // Initialize sort select
    this.sortSelect.addEventListener('change', () => this.filterAndSortViewers());

    // Initialize refresh button
    const refreshButton = this.container.querySelector('.viewers-header .refresh-button');
    if (refreshButton) {
      refreshButton.addEventListener('click', () => 
        this.refreshViewers(refreshButton as HTMLButtonElement)
      );
    }

    // Initialize filter buttons
    const filterButtonsContainer = this.container.querySelector('.filter-buttons');
    if (!filterButtonsContainer) {
      console.warn('Filter buttons container not found');
      return;
    }

    const filterButtons = filterButtonsContainer.querySelectorAll('.filter-button');
    console.log('Found filter buttons:', filterButtons.length);
    
    // Add icons and set up click handlers for filter buttons
    filterButtons.forEach((button: Element) => {
      const filterType = button.getAttribute('data-filter');
      console.log('Setting up filter button:', filterType);
      
      // Add icons and labels to buttons
      if (button instanceof HTMLElement) {
        let icon = '';
        let label = '';
        switch(filterType) {
          case 'all': 
            icon = 'group';
            label = 'All';
            break;
          case 'moderator': 
            icon = 'security';
            label = 'Mods';
            break;
          case 'vip': 
            icon = 'diamond';
            label = 'VIPs';
            break;
          case 'bot': 
            icon = 'smart_toy';
            label = 'Bots';
            break;
          case 'viewer': 
            icon = 'visibility';
            label = 'Viewers';
            break;
        }
        button.innerHTML = `<span class="material-symbols-outlined">${icon}</span><span>${label}</span>`;
      }
      
      const clickHandler = (e: Event) => {
        e.preventDefault();
        console.log('Filter button clicked:', filterType);
        
        if (filterType) {
          // Update current filter
          this.currentFilter = filterType;
          
          // Update button states
          filterButtons.forEach(btn => {
            if (btn instanceof HTMLElement) {
              btn.classList.remove('active');
            }
          });
          
          if (button instanceof HTMLElement) {
            button.classList.add('active');
          }
          
          // Apply filtering
          this.filterAndSortViewers();
        }
      };

      // Remove old listener if exists and add new one
      button.removeEventListener('click', clickHandler);
      button.addEventListener('click', clickHandler);
    });

    // Set initial filter to 'all'
    const allFilterButton = filterButtonsContainer.querySelector('.filter-button[data-filter="all"]');
    if (allFilterButton instanceof HTMLElement) {
      console.log('Setting initial filter to all');
      this.currentFilter = 'all';
      allFilterButton.classList.add('active');
    } else {
      console.warn('Could not find all filter button');
    }
  }

  private updateViewerCounts(): void {
    // Ensure metrics container has the right structure
    const metricsContainer = this.container.querySelector('.viewers-metrics');
    if (metricsContainer) {
      // Clear existing content
      metricsContainer.innerHTML = '';
      
      // Create content wrapper
      const metricsContent = document.createElement('div');
      metricsContent.className = 'viewers-metrics-content';
      
      // Create metric elements with data-label attributes
      const totalMetric = document.createElement('div');
      totalMetric.setAttribute('data-label', 'Total Viewers');
      totalMetric.innerHTML = '<span>Total Viewers:</span> <span id="totalViewers" class="metric-value"></span>';
      
      const realMetric = document.createElement('div');
      realMetric.setAttribute('data-label', 'Real Viewers');
      realMetric.innerHTML = '<span>Real Viewers:</span> <span id="realViewers" class="metric-value"></span>';
      
      const botsMetric = document.createElement('div');
      botsMetric.setAttribute('data-label', 'Known Bots');
      botsMetric.innerHTML = '<span>Known Bots:</span> <span id="knownBots" class="metric-value"></span>';

      
      metricsContent.appendChild(totalMetric);
      metricsContent.appendChild(realMetric);
      metricsContent.appendChild(botsMetric);
      metricsContainer.appendChild(metricsContent);
      
      // Update element references
      this.totalViewersElement = metricsContent.querySelector('#totalViewers') as HTMLElement;
      this.realViewersElement = metricsContent.querySelector('#realViewers') as HTMLElement;
      this.knownBotsElement = metricsContent.querySelector('#knownBots') as HTMLElement;
    }

    const counts = this.viewersService.getViewerCounts(this.viewers);
    this.totalViewersElement.textContent = counts.total.toString();
    this.realViewersElement.textContent = counts.real.toString();
    this.knownBotsElement.textContent = counts.bots.toString();
  }

  private getBadgeUrl(type: Viewer['type']): string {
    switch(type) {
      case 'broadcaster':
        return 'https://static-cdn.jtvnw.net/badges/v1/5527c58c-fb7d-422d-b71b-f309dcb85cc1/3';
      case 'moderator':
        return 'https://static-cdn.jtvnw.net/badges/v1/3267646d-33f0-4b17-b3df-f923a41db1d0/3';
      case 'vip':
        return 'https://static-cdn.jtvnw.net/badges/v1/b817aba4-fad8-49e2-b88a-7cc744dfa6ec/3';
      default:
        return '';
    }
  }

  private renderViewerItem(viewer: Viewer): string {
    const badgeUrl = this.getBadgeUrl(viewer.type);
    const badgeHtml = badgeUrl ? `<img src="${badgeUrl}" alt="${viewer.type}" class="viewer-badge">` : '';

    return `
      <div class="viewer-card${this.selectedViewerId === viewer.id ? ' active' : ''}" data-user-id="${viewer.id}">
        <div class="viewer-header">
          <div class="viewer-avatar">
            <img src="${viewer.profileImageUrl || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/cdd517fe-def4-11e9-948e-784f43822e80-profile_image-70x70.png'}" alt="${viewer.displayName}">
          </div>
           <div class="viewer-info">
            <div class="viewer-name"><span class="viewer-badge">${badgeHtml}</span> ${viewer.displayName}</div>
            <div class="viewer-type ${viewer.isBot ? 'bot' : ''}">${viewer.isBot ? 'Bot' : 'Viewer'}</div>
          </div>
           <!-- Removed unnecessary comments -->
        </div>
        <div class="viewer-actions">
          <div class="actions-content">

            <button class="action-button raid" data-action="raid">
              <span class="material-symbols-outlined">rocket_launch</span>
              <span class="button-text">Raid</span>
            </button>
            <button class="action-button ban" data-action="ban">
              <span class="material-symbols-outlined">block</span>
              <span class="button-text">Ban</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private selectedViewerId: string | null = null;

  private filterAndSortViewers(): void {
    console.log('Filtering and sorting viewers');
    console.log('Current filter:', this.currentFilter);
    console.log('Total viewers before filter:', this.viewers.length);

    // Add active class to previously selected viewer if it still exists
    if (this.selectedViewerId) {
      const selectedItem = this.viewersList.querySelector(`.viewer-item[data-user-id="${this.selectedViewerId}"]`);
      if (selectedItem) {
        selectedItem.classList.add('active');
      }
    }

    const searchTerm = this.searchInput.value.toLowerCase();
    const sortBy = this.sortSelect.value;

    let filteredViewers = this.viewers.filter(viewer => {
      // Handle search term
      const matchesSearch = viewer.displayName.toLowerCase().includes(searchTerm) ||
                          viewer.username.toLowerCase().includes(searchTerm);
      
      // Handle filter type
      let matchesFilter = false;
      switch (this.currentFilter) {
        case 'all':
          matchesFilter = true;
          break;
        case 'moderator':
          matchesFilter = viewer.type === 'moderator';
          break;
        case 'vip':
          matchesFilter = viewer.type === 'vip';
          break;
        case 'viewer':
          matchesFilter = viewer.type === 'viewer' && viewer.isBot === false;
          break;
        case 'bot':
          matchesFilter = viewer.isBot === true; // Check if the viewer is a bot
          break;
        default: 
          console.warn('Unknown filter type:', this.currentFilter);
          matchesFilter = true;
          break; // Prevent fall-through
      }

      const result = matchesSearch && matchesFilter;
      return result;
    });

    console.log('Filtered viewers:', filteredViewers.length);

    filteredViewers.sort((a, b) => {
      switch(sortBy) {
        case 'username':
          return a.displayName.localeCompare(b.displayName);
        case 'type':
          return a.type.localeCompare(b.type);
        case 'joined':
          return b.joinedAt.getTime() - a.joinedAt.getTime();
        default:
          return 0;
      }
    });

    // Clear existing content
    this.viewersList.innerHTML = '';
    
    // Add filtered and sorted viewers
    const viewersHtml = filteredViewers.map(viewer => 
      this.renderViewerItem(viewer)
    ).join('');
    this.viewersList.innerHTML = viewersHtml;

    // Restore active state if needed
    if (this.selectedViewerId) {
      const selectedItem = this.viewersList.querySelector(`.viewer-item[data-user-id="${this.selectedViewerId}"]`);
      if (selectedItem) {
        selectedItem.classList.add('active');
      }
    }

    console.log('Viewer list updated');
  }

  // Method removed as it's not being used

  private async handleViewerClick(e: MouseEvent): Promise<void> {
    const target = e.target as HTMLElement;
    const viewerCard = target.closest('.viewer-card');
    
    if (!viewerCard) return;

    // Don't handle clicks on buttons
    if (target.closest('button')) {
      return;
    }

    // Handle card click (expand/collapse)
    const userId = viewerCard.getAttribute('data-user-id');
    if (userId) {
      if (this.selectedViewerId === userId) {
        this.selectedViewerId = null;
        viewerCard.classList.remove('active');
      } else {
        this.selectedViewerId = userId;
        this.viewersList.querySelectorAll('.viewer-card').forEach(card => 
          card.classList.remove('active')
        );
        viewerCard.classList.add('active');
      }
    }
  }

  private async handlePreviewButtonClick(e: MouseEvent): Promise<void> {
    const target = e.target as HTMLElement;
    const button = target.closest('.action-button') as HTMLButtonElement;
    const viewerCard = target.closest('.viewer-card');
    
    if (!button || !viewerCard) return;

    const action = button.getAttribute('data-action');
    const userId = viewerCard.getAttribute('data-user-id');
    const viewer = this.viewers.find(v => v.id === userId);
    
    if (!userId || !action || !viewer) return;

    try {
      button.disabled = true;
      
      switch (action) {
        case 'raid':
          const result = await Swal.fire({
            title: 'Start Raid?',
            html: `Are you sure you want to raid <strong>${viewer.displayName}</strong>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Start Raid!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#9147ff',
            cancelButtonColor: '#6e7681',
            background: '#18181b',
            color: '#ffffff',
            width: '360px'
          });

          if (result.isConfirmed) {
            try {
            const buttonText = button.querySelector('.button-text');
            if (buttonText) buttonText.textContent = 'Starting Raid...';
              await this.viewersService.startRaid(this.currentUser.id, userId);
              
              await Swal.fire({
                title: 'Raid Started!',
                html: `Successfully started raid to <strong>${viewer.displayName}</strong>`,
                icon: 'success',
                timer: 2000,
                timerProgressBar: true,
                confirmButtonColor: '#9147ff',
                background: '#18181b',
                color: '#ffffff',
                width: '360px'
              });
            } catch (error) {
              console.error('Failed to start raid:', error);
              
              const errorMessage = error instanceof Error ? error.message : 'Unable to start the raid. Please try again.';
              await Swal.fire({
                title: 'Unable to Start Raid',
                html: `<div style="text-align: left; margin-top: 1rem;">${errorMessage}</div>`,
                icon: 'error',
                confirmButtonColor: '#9147ff',
                background: '#18181b',
                color: '#ffffff',
                width: '360px'
              });
            }
          }
          break;
          
        case 'ban':
          const confirmed = confirm(`Are you sure you want to ban ${viewer.displayName}?`);
          if (confirmed) {
            await this.viewersService.banUser(this.currentUser.id, userId);
            console.log(`Banned ${viewer.displayName}`);
            await this.refreshViewers();
          }
          break;
      }
    } catch (error) {
      console.error(`Failed to perform ${action} action:`, error);
    } finally {
      const buttonText = button.querySelector('.button-text');
      if (buttonText && buttonText.textContent === 'Starting Raid...') {
        buttonText.textContent = 'Raid';
      }
      button.disabled = false;
    }
  }

  private async refreshViewers(button?: HTMLButtonElement): Promise<void> {
    const currentUser = twitchService.getUser();
    if (!currentUser) return;

    try {
      if (button) {
        button.classList.add('loading');
      }
      
      // Fetch viewers and ensure we have data before proceeding
      const viewers = await this.viewersService.getChannelViewers(currentUser.id);
      if (!viewers || viewers.length === 0) {
        console.warn('No viewers data received');
        return;
      }
      
      this.viewers = viewers;
      this.updateViewerCounts();
      this.filterAndSortViewers();
    } catch (error) {
      console.error('Failed to fetch viewers:', error);
    } finally {
      if (button) {
        button.classList.remove('loading');
      }
    }
  }

  public async initialize(): Promise<() => void> {
    console.log('Initializing ViewersComponent');

    try {
      // Add collapse button
      const collapseButton = document.createElement('button');
      collapseButton.className = 'collapse-button';
      collapseButton.innerHTML = '<i class="material-symbols-outlined">expand_less</i>';
      collapseButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.viewersHeader.classList.toggle('collapsed');
      };
      this.viewersHeader.appendChild(collapseButton);
      this.viewersHeader.classList.add('collapsed'); // Set collapsed by default

      // Initial load
      await this.refreshViewers();

      // Initialize other event listeners
      this.initializeEventListeners();

      // Force initial filter and sort after a short delay to ensure DOM is ready
      setTimeout(() => {
        console.log('Applying initial filter and sort');
        this.filterAndSortViewers();
      }, 100);

      // Refresh every 15 seconds
      const refreshInterval = setInterval(() => this.refreshViewers(), 15000);

      // Return cleanup function
      return () => {
        console.log('Cleaning up ViewersComponent');
        clearInterval(refreshInterval);
      };
    } catch (error) {
      console.error('Error initializing ViewersComponent:', error);
      throw error;
    }
  }
}
